import { useCallback, useContext, useMemo } from 'react';
import { ConfigContext } from '@common/Config';
import { parseJSON } from '@helpers/data';
import TranslationsContext from './context';
import { getTranslation, isShowTranslationKeysRequested } from './helpers';

function TranslationsProvider({ context, children }) {
    const configContext = useContext(ConfigContext);
    const isShowKeys = isShowTranslationKeysRequested(configContext);

    const translations = useMemo(() => {
        if (isShowKeys) {
            return {};
        }

        const translationsJson = context.translations
            // eslint-disable-next-line no-undef
            || (typeof translationsMapSsr !== 'undefined' && translationsMapSsr[`${context.store}/${context.language}`]);

        if (translationsJson) {
            const parsedTranslations = parseJSON(translationsJson, {});

            if (Object.keys(parsedTranslations).length > 0) {
                return parsedTranslations;
            }
        }

        // Check for App variable existence is here, because BE functional tests do trigger SSR
        // without translations context resulting in errors about missing App variable.
        // We need to return empty translations object in that case, for translation key to be used
        // as a translation.
        if (typeof App !== 'undefined' && App.Translations && App.Translations.dictionary) {
            return App.Translations.dictionary[App.Config.language];
        }

        return {};
    }, [
        context.translations,
        context.store,
        context.language,
        isShowKeys
    ]);

    const translate = useCallback(
        (key, parameters) => getTranslation(key, parameters, translations),
        [translations]
    );

    const providerValue = useMemo(
        () => ({ translate, translations }),
        [translate, translations]
    );

    return (
        <TranslationsContext.Provider value={providerValue}>
            {children}
        </TranslationsContext.Provider>
    );
}

export default TranslationsProvider;
